import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { PublicShell } from "../components/PublicShell";
import { getArticle, ARTICLES } from "../data/articles";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} | TaperPath`;
    setMeta("description", article.description);
    setJsonLd(article);
    return () => { document.title = "TaperPath"; };
  }, [article]);

  if (!article) return <Navigate to="/articles" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
  const Content = article.Content;

  return (
    <PublicShell>
      <article className="mx-auto max-w-3xl px-6 py-10 lg:py-14">
        <Link to="/articles" data-testid="article-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A7c59] hover:underline">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <div className="mt-6 flex items-center gap-3 text-xs font-medium text-muted-foreground">
          <span className="rounded-full bg-[#4A7c59]/10 px-2.5 py-0.5 text-[#4A7c59]">{article.category}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.readTime}</span>
        </div>

        <h1 className="mt-3 font-head text-3xl font-extrabold leading-tight text-[#2C363F] sm:text-4xl" data-testid="article-title">{article.title}</h1>

        <div className="mt-6 overflow-hidden rounded-2xl shadow-lg shadow-[#4A7c59]/10">
          <img src={article.image} alt={article.title} className="h-64 w-full object-cover sm:h-80" />
        </div>

        <div className="article-prose mt-8" data-testid="article-body">
          <Content />
        </div>

        <div className="mt-10 rounded-2xl border border-[#4A7c59]/20 bg-[#4A7c59]/5 p-6 text-center">
          <h3 className="font-head text-lg font-semibold text-[#2C363F]">Ready to take a gentle first step?</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Try the free Taper Tool, or create a private account to track your journey — anonymously, with email, or with Google.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Link to="/taper" className="rounded-full bg-[#4A7c59] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">Open Taper Tool</Link>
            <Link to="/auth" className="rounded-full border border-[#4A7c59]/30 bg-white px-5 py-2.5 text-sm font-semibold text-[#4A7c59] transition-transform duration-300 hover:-translate-y-0.5">Create free account</Link>
          </div>
        </div>
      </article>

      <section className="mx-auto max-w-5xl px-6 pb-8">
        <h2 className="font-head text-2xl font-bold text-[#2C363F]">Keep reading</h2>
        <div className="mt-4 grid gap-5 sm:grid-cols-3">
          {related.map((a) => (
            <Link key={a.slug} to={`/articles/${a.slug}`} className="group rounded-2xl border border-border bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <span className="text-xs font-medium text-[#4A7c59]">{a.category}</span>
              <h3 className="mt-2 font-head text-base font-semibold leading-snug text-[#2C363F]">{a.title}</h3>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4A7c59]">Read <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
      </section>
    </PublicShell>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setJsonLd(article) {
  const id = "article-jsonld";
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.image,
    author: { "@type": "Organization", name: "TaperPath" },
    publisher: { "@type": "Organization", name: "TaperPath" },
  });
}
